import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { log } from '../lib/logger';
import { Button } from '../components/ui/button';
import JourneyStepper from '../components/JourneyStepper';
import { ClaimAccountCard } from '../components/ClaimAccountCard';
import { PaymentCapturedBadge } from '../components/PaymentCapturedBadge';
import { CheckCircle, AlertTriangle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isGuest, loading } = useAuth();
  const [orderNumber, setOrderNumber] = useState('');
  const [poNumber, setPONumber] = useState('');
  // `docsError` mirrors the backend `enhanced_orders.docs_error` field. It
  // gets stamped by the /submit background task when PDF proof generation
  // fails. We poll the order briefly so a failure that happens a few
  // seconds after submit still renders the warning banner.
  const [docsError, setDocsError] = useState(null);
  // Mirror the `payment_status` + `payment_captured_at` fields so the
  // page can light up a "Payment captured ✓" badge the moment the
  // backend's auto-capture task lands. Polled alongside docs_error.
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentCapturedAt, setPaymentCapturedAt] = useState(null);
  // iter109: ISO ship-date string ("YYYY-MM-DD") rendered above the
  // victory video. Sourced from the same poll since /enhanced-orders/{id}
  // now stamps `estimated_ship_date` on every fetch.
  const [estimatedShipDate, setEstimatedShipDate] = useState(null);
  const proofApproved = !!location.state?.proofApproved;
  const orderId = location.state?.orderId;

  useEffect(() => {
    // Wait for AuthContext's initial /auth/me probe to finish before any
    // redirect decisions — otherwise a cold-load authenticated guest would
    // momentarily be treated as logged-out and bounced to /login.
    if (loading) return undefined;
    if (!isAuthenticated) {
      navigate('/login');
      return undefined;
    }

    if (location.state?.orderNumber) {
      setOrderNumber(location.state.orderNumber);
    }

    if (location.state?.poNumber) {
      setPONumber(location.state.poNumber);
    }

    // Poll the order record for ~15s after submit so we can show the
    // `docs_error` banner if the background PDF generator fails. Stops as
    // soon as an error appears or `generated_proof_id` is stamped (whichever
    // comes first). Clean-up cancels the loop on unmount.
    if (!orderId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/enhanced-orders/${orderId}`);
        if (cancelled) return;
        if (res.data?.docs_error) {
          setDocsError(res.data.docs_error);
        }
        // Surface the auto-capture result the instant it lands. Polling
        // catches the (~1-3 s) gap between /submit returning and the
        // background `auto_capture_if_authorized` task flipping
        // payment_status to 'captured'.
        if (res.data?.payment_status) {
          setPaymentStatus(res.data.payment_status);
        }
        if (res.data?.payment_captured_at) {
          setPaymentCapturedAt(res.data.payment_captured_at);
        }
        if (res.data?.estimated_ship_date) {
          setEstimatedShipDate(res.data.estimated_ship_date);
        }
      } catch (pollErr) {
        // Non-fatal — banner just stays hidden until the next 3s tick.
        log.debug('[OrderConfirmation] payment status poll failed:', pollErr?.response?.status || pollErr?.name);
      }
    };
    const iv = setInterval(poll, 3000);
    poll();
    const stop = setTimeout(() => clearInterval(iv), 15000);

    return () => {
      cancelled = true;
      clearInterval(iv);
      clearTimeout(stop);
    };
  }, [loading, isAuthenticated, isGuest, location, navigate, orderId]);

  // Auto-redirect effect — SEPARATE from the poll effect so the
  // `docsError` dep re-runs this hook whenever the poll sets the banner.
  // Fixes an earlier stale-closure bug where the timer always saw
  // `docsError=null` from the initial render and redirected even when the
  // amber banner had just appeared.
  useEffect(() => {
    if (loading || !isAuthenticated || isGuest || docsError) return undefined;
    const redirectTimer = setTimeout(() => {
      navigate('/po-history');
    }, 4000);
    return () => clearTimeout(redirectTimer);
  }, [loading, isAuthenticated, isGuest, docsError, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* iter114s.45 — Order Confirmation lives at the END of stage 5
          (Approve Proof) — by this point the customer has approved
          the proof and the order is heading to production. Per-page
          brand band removed for chrome consistency. */}
      <JourneyStepper currentStage={5} orderId={orderId} />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          {/* iter109: prominent "Estimated ship" pill placed ABOVE the
              victory video so the customer sees the most actionable
              question ("when does my order ship?") answered before
              their eye even reaches the celebration animation. The pill
              is rendered only when the backend has computed a date
              (set on /enhanced-orders/{id}.estimated_ship_date). */}
          {estimatedShipDate && (
            <div className="flex justify-center mb-6" data-testid="order-confirmation-ship-date-wrap">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 border border-green-300 text-green-800 text-sm font-semibold shadow-sm"
                data-testid="order-confirmation-ship-date"
              >
                <Clock className="w-4 h-4" />
                Estimated ship: {estimatedShipDate}
              </span>
            </div>
          )}

          {/* Victory hero (iter105): a 2.5-second branded knight loop
              with the sword raised triumphantly. Replaces the static
              CheckCircle as the first thing the customer sees on the
              confirmation page — psychologically closes the loop
              ("my order is done — the herald celebrates"). 118 KB MP4
              with a 20 KB JPG poster so the placeholder shows
              instantly even before the video buffers. Falls back to
              the original CheckCircle if the customer's browser /
              network blocks autoplaying video. */}
          <div
            className="flex justify-center mb-6"
            data-testid="order-confirmation-victory"
          >
            <video
              src="/eg3-knight-victory.mp4"
              poster="/eg3-knight-victory-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
              className="h-44 w-auto rounded-lg shadow-lg pointer-events-none select-none"
              style={{
                filter:
                  'drop-shadow(0 0 18px rgba(74,255,89,0.45)) drop-shadow(0 0 36px rgba(74,255,89,0.22))',
              }}
              data-testid="order-confirmation-victory-video"
            >
              {/* Fallback for browsers that don't render the <video>
                  source — the CheckCircle still appears via the
                  poster image which we've stamped with the knight
                  raised-sword frame. */}
              <CheckCircle className="w-24 h-24 text-green-500" />
            </video>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 mb-6">Thank you for your order!</h1>

          {proofApproved && (
            <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-300 rounded-full text-green-800 text-sm font-semibold" data-testid="proof-approved-badge">
              <CheckCircle className="w-4 h-4" />
              Proof customer-approved — heading to production
            </div>
          )}

          {/* Payment captured pill — appears the moment the backend's
              Converge auto-capture task lands (typically 1-3 s after
              /submit). Closes the customer's psychological loop:
              "I approved → I got charged → my order is in motion." */}
          {paymentStatus === 'captured' && (
            <div className="mb-6 flex justify-center" data-testid="order-confirmation-payment-captured-wrap">
              <PaymentCapturedBadge
                paymentStatus={paymentStatus}
                capturedAt={paymentCapturedAt}
                testId="order-confirmation-payment-captured"
              />
            </div>
          )}

          {docsError && (
            <div
              className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg text-left"
              data-testid="order-confirmation-docs-error"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-amber-800 mb-1">
                    Proof generation hit a snag
                  </p>
                  <p className="text-amber-900 mb-2">
                    We couldn&apos;t auto-generate your digital proof PDF, but <strong>your order was received successfully</strong>. Your account manager has been notified and will regenerate the proof manually within 1 business day.
                  </p>
                  <p className="text-xs text-amber-700 font-mono truncate" title={docsError}>
                    Technical detail: {docsError}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4 mb-8 text-lg">
            <p className="text-gray-700">
              Your order request is <span className="font-bold text-[#4A7C59]">{orderNumber || 'processing'}</span>
            </p>
            {poNumber && (
              <p className="text-gray-700">
                Your Customer PO Number is: <span className="font-bold">{poNumber}</span>
              </p>
            )}
          </div>

          <div className="bg-blue-50 border-l-4 border-[#4A7C59] p-6 mb-8 text-left">
            <p className="text-gray-700 mb-4 font-semibold text-center">
              {isGuest
                ? 'Your order is saved. Finish the quick step below before you leave.'
                : docsError
                  ? 'Your order is saved — see the warning above. You can stay on this page or navigate away at any time.'
                  : (<>🎉 Redirecting to your proof in <span className="text-[#4A7C59]">4 seconds</span>...</>)
              }
            </p>
            {!isGuest && !docsError && (
              <div className="flex justify-center mb-4">
                <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-[#4A7C59] rounded-full animate-[progress_4s_ease-in-out]"></div>
                </div>
              </div>
            )}
            <p className="text-gray-700 mb-2 text-sm">
              <span className="font-semibold">What happens next?</span>
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 text-sm">
              <li>Review your digital proof for approval</li>
              <li>Your account manager will contact you via e-mail</li>
              <li>Once approved, your order will go into production</li>
              <li>You'll receive tracking information when your order ships</li>
            </ul>
          </div>

          <ClaimAccountCard />

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/po-history')}
              className="bg-[#4A7C59] hover:bg-[#3d6849] text-white px-8 py-3"
            >
              View Your Order History Now
            </Button>
            <Button
              onClick={() => navigate('/splash')}
              className="bg-white hover:bg-gray-50 text-[#4A7C59] border-2 border-[#4A7C59] px-8 py-3"
            >
              Return to Home
            </Button>
          </div>
        </div>

        <style>{`
          @keyframes progress {
            from { width: 0%; }
            to { width: 100%; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
